import { Link } from "react-router-dom";
import { clsx } from "clsx";

import type { PipelineBoardCard } from "../../types";
import { prioOf } from "../../lib/tags";
import { dwellDays, isStale } from "../../lib/pipelineBoard";

// Pipeline-flavored card content: value/probability meta instead of the
// campaign card's task actions, plus a prio badge and a stage-dwell line.
export function PipelineCardBody({ card }: { card: PipelineBoardCard }) {
  const meta: string[] = [];
  if (card.value_eur != null) {
    meta.push(`€${(card.value_eur / 100).toLocaleString("de-DE")}`);
  }
  if (card.probability != null) {
    meta.push(`${card.probability}%`);
  }

  const prio = prioOf(card.tags);
  const dwell = dwellDays(card);
  const stale = isStale(card);
  const ix = card.interactions_since_stage_entry ?? 0;
  const parked = card.status === "parked";

  const dwellParts: string[] = [];
  if (parked) {
    dwellParts.push(`parked ${card.parked_at?.slice(0, 10) ?? ""}`.trim());
    if (card.parked_until) dwellParts.push(`until ${card.parked_until}`);
  } else if (dwell != null) {
    dwellParts.push(`${dwell}d in stage`);
  }
  dwellParts.push(`${ix} ix`);

  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <Link
          to={`/contacts/${card.contact_id}`}
          className="kcard-name truncate hover:text-aqua-medium"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {card.name || card.contact_id}
        </Link>
        <div className="flex items-baseline gap-1.5 shrink-0">
          {parked ? <span className="pill pill--muted">parked</span> : null}
          {prio != null ? (
            <span
              className={clsx(
                "pill",
                prio <= 1 ? "pill--signal" : "pill--muted",
              )}
            >
              <span className="tnum">P{prio}</span>
            </span>
          ) : null}
          {meta.length ? (
            <span className="text-xs tnum text-ink-low">
              {meta.join(" · ")}
            </span>
          ) : null}
        </div>
      </div>
      <div className="kcard-meta truncate">
        {parked && card.parked_reason
          ? card.parked_reason
          : card.next_step
            ? card.next_step
            : card.enrolled_at
              ? `since ${card.enrolled_at.slice(0, 10)}`
              : "—"}
      </div>
      <div
        className={clsx(
          "kcard-meta tnum truncate",
          stale ? "text-yellow-dark" : null,
        )}
      >
        {dwellParts.join(" · ")}
      </div>
    </>
  );
}
